import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getOrganizations, createOrganization, deleteOrganization, createOrgOwner, getSuperadminStats } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { OrganizationDetailModal } from '../modals/OrganizationDetailModal';
import { Building2, Users, Target, CheckSquare, Plus, Trash2, UserPlus, Loader2, Crown, Settings } from 'lucide-react';
import { toast } from 'sonner';

const planColors = {
  starter: 'bg-slate-500/20 text-slate-400',
  pro: 'bg-cyan-500/20 text-cyan-400',
  business: 'bg-violet-500/20 text-violet-400',
  enterprise: 'bg-yellow-500/20 text-yellow-400',
};

export const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [showOrgDetailModal, setShowOrgDetailModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', plan: 'starter' });
  const [newOwner, setNewOwner] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [orgsRes, statsRes] = await Promise.all([
        getOrganizations(),
        getSuperadminStats()
      ]);
      setOrganizations(orgsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateOrg = async () => {
    if (!newOrg.name || !newOrg.slug) {
      toast.error('Name and slug are required');
      return;
    }
    setCreating(true);
    try {
      await createOrganization(newOrg.name, newOrg.slug.toLowerCase().replace(/\s+/g, '-'), newOrg.plan);
      toast.success(`Organization "${newOrg.name}" created`);
      setShowCreateOrgModal(false);
      setNewOrg({ name: '', slug: '', plan: 'starter' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`DELETE "${org.name}" and ALL its data? This cannot be undone!`)) return;
    try {
      await deleteOrganization(org.id);
      toast.success('Organization deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete organization');
    }
  };

  const handleAddOwner = async () => {
    if (!newOwner.name || !newOwner.email || !newOwner.password) {
      toast.error('All fields are required');
      return;
    }
    setCreating(true);
    try {
      await createOrgOwner(selectedOrg.id, newOwner.name, newOwner.email, newOwner.password);
      toast.success(`Owner "${newOwner.name}" added to ${selectedOrg.name}`);
      setShowAddOwnerModal(false);
      setNewOwner({ name: '', email: '', password: '' });
      setSelectedOrg(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add owner');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              Super Admin Dashboard
            </h1>
            <p className="text-muted-foreground">Manage all organizations and users</p>
          </div>
          <Button onClick={() => setShowCreateOrgModal(true)} className="bg-accent hover:bg-accent/90">
            <Plus className="w-4 h-4 mr-2" />
            New Organization
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 p-6 border-b">
          <div className="bg-card rounded-lg p-4 border-l-4 border-cyan-500">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Building2 className="w-4 h-4" />
              <span className="text-sm">Organizations</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_organizations}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border-l-4 border-green-500">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total Users</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_users}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border-l-4 border-violet-500">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Target className="w-4 h-4" />
              <span className="text-sm">Total Leads</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_leads}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border-l-4 border-orange-500">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckSquare className="w-4 h-4" />
              <span className="text-sm">Total Tasks</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_tasks}</p>
          </div>
        </div>
      )}

      {/* Organizations List */}
      <ScrollArea className="flex-1 p-6">
        <h2 className="text-lg font-semibold mb-4">Organizations ({organizations.length})</h2>
        
        {organizations.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No organizations yet</p>
            <p className="text-sm text-muted-foreground/70">Create your first organization to get started</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {organizations.map((org) => (
              <div key={org.id} className="bg-card rounded-lg p-4 border hover:border-accent/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{org.name}</h3>
                      <Badge className={planColors[org.plan]}>{org.plan.toUpperCase()}</Badge>
                      {!org.is_active && <Badge variant="destructive">INACTIVE</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">/{org.slug}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {org.user_count} users
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-4 h-4" /> {org.lead_count} leads
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckSquare className="w-4 h-4" /> {org.task_count} tasks
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedOrg(org);
                        setShowOrgDetailModal(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Features
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedOrg(org);
                        setShowAddOwnerModal(true);
                      }}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Owner
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteOrg(org)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Create Organization Modal */}
      <Dialog open={showCreateOrgModal} onOpenChange={setShowCreateOrgModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>Add a new business to the platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                placeholder="Acme Corp"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (URL-friendly)</Label>
              <Input
                placeholder="acme-corp"
                value={newOrg.slug}
                onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={newOrg.plan} onValueChange={(val) => setNewOrg({ ...newOrg, plan: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter ($29/mo)</SelectItem>
                  <SelectItem value="pro">Pro ($79/mo)</SelectItem>
                  <SelectItem value="business">Business ($149/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (Custom)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateOrgModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateOrg} disabled={creating} className="flex-1 bg-accent hover:bg-accent/90">
                {creating ? 'Creating...' : 'Create Organization'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Owner Modal */}
      <Dialog open={showAddOwnerModal} onOpenChange={setShowAddOwnerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Organization Owner</DialogTitle>
            <DialogDescription>
              Create an admin account for {selectedOrg?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="John Smith"
                value={newOwner.name}
                onChange={(e) => setNewOwner({ ...newOwner, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@company.com"
                value={newOwner.email}
                onChange={(e) => setNewOwner({ ...newOwner, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Minimum 6 characters"
                value={newOwner.password}
                onChange={(e) => setNewOwner({ ...newOwner, password: e.target.value })}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddOwnerModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleAddOwner} disabled={creating} className="flex-1 bg-accent hover:bg-accent/90">
                {creating ? 'Adding...' : 'Add Owner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Organization Detail Modal */}
      <OrganizationDetailModal
        open={showOrgDetailModal}
        onClose={() => setShowOrgDetailModal(false)}
        organization={selectedOrg}
        onUpdate={fetchData}
      />
    </div>
  );
};
